import React, { useRef } from 'react';
import { motion, useInView } from "motion/react";

interface FeatureSectionProps {
  title: string;
  body: string;
  image: string;
  layout?: 'imageLeft' | 'imageRight';
  children?: React.ReactNode;
}

const FeatureSection: React.FC<FeatureSectionProps> = ({ 
  title, 
  body, 
  image, 
  layout = 'imageRight', 
  children 
}) => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.3 });

  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({ 
      opacity: 1, 
      y: 0, 
      transition: { 
        delay: i * 0.2, 
        duration: 0.6, 
        ease: "easeOut" as const 
      } 
    })
  };
  
  // Determine grid layout classes based on the layout prop
  const gridLayoutClasses = layout === 'imageRight' 
    ? "grid-cols-1 lg:grid-cols-3" 
    : "grid-cols-1 lg:grid-cols-3";
  const textOrderClasses = layout === 'imageRight' 
    ? "lg:col-span-2" 
    : "lg:col-span-2 lg:col-start-2";
  const childrenOrderClasses = layout === 'imageRight' 
    ? "lg:col-span-1" 
    : "lg:col-span-1 lg:row-start-1";

  return (
    <section 
      ref={sectionRef} 
      id="details" 
      className="max-w-8xl mx-auto px-8 py-32 bg-black relative z-10 overflow-hidden"
    >
      <motion.div 
        className="absolute inset-0 z-0 opacity-10" 
        style={{ y: '-30%' }}
      >
        <div className="flex items-center justify-center bg-gray-800 text-gray-500 rounded-full w-96 h-96 mx-auto">
          <span className="text-sm tracking-widest uppercase">{image}</span>
        </div>
      </motion.div>

      <div className={`grid ${gridLayoutClasses} gap-16 relative z-10`}>
        <div className={textOrderClasses}>
          <motion.h2 
            custom={0}
            variants={textVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="text-4xl font-serif font-medium text-white mb-6"
          >
            {title}
          </motion.h2>
          <motion.div 
            custom={1}
            variants={textVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="text-gray-300 leading-relaxed whitespace-pre-line space-y-4"
          >
            {body.split('\n\n').map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </motion.div>
        </div>
        {children && (
          <div className={childrenOrderClasses}>
            {children}
          </div>
        )}
      </div>
    </section>
  );
};

export default FeatureSection;
